import React, { useEffect, useRef, useState } from "react";
import * as XLSX from "xlsx";
import {
  formatDateOnly,
  formatTimestampUtc,
} from "../../utils/dataExchangeDates";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function ExportButton({
  getExportData,
  label = "Export",
  disabled = false,
  dropUp = false,
}) {
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    const handleDocumentClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleDocumentClick);

    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
    };
  }, []);

  const loadData = async () => {
    if (typeof getExportData !== "function") {
      alert("Export service is not configured.");
      return null;
    }

    try {
      setLoading(true);

      const response = await getExportData();

      const data = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.leads)
            ? response.leads
            : [];

      if (data.length === 0) {
        alert("No data to export.");
        return null;
      }

      return data;
    } catch (err) {
      console.error("Failed to load export data:", err);

      alert(err?.response?.data?.error || "Failed to load export data");

      return null;
    } finally {
      setLoading(false);
    }
  };

  const getContactName = (lead) => {
    const contact = Array.isArray(lead?.contacts) ? lead.contacts[0] : null;

    if (!contact) {
      return "";
    }

    return [
      contact.first_name || contact.firstName,
      contact.last_name || contact.lastName,
    ]
      .filter(Boolean)
      .join(" ");
  };

  const getDmsProviders = (lead) => {
    if (Array.isArray(lead?.dms_providers)) {
      return lead.dms_providers;
    }

    if (Array.isArray(lead?.dmsProviders)) {
      return lead.dmsProviders;
    }

    if (typeof lead?.dms_providers === "string") {
      try {
        const parsed = JSON.parse(lead.dms_providers);

        return Array.isArray(parsed) ? parsed : [lead.dms_providers];
      } catch {
        return [lead.dms_providers];
      }
    }

    return [];
  };

  const getProducts = (lead) => {
    if (Array.isArray(lead?.products)) {
      return lead.products;
    }

    if (typeof lead?.products === "string") {
      try {
        const parsed = JSON.parse(lead.products);

        return Array.isArray(parsed) ? parsed : [lead.products];
      } catch {
        return [lead.products];
      }
    }

    return [];
  };

  const getManufacturers = (lead) => {
    if (Array.isArray(lead?.manufacturer)) {
      return lead.manufacturer;
    }

    if (Array.isArray(lead?.manufacturers)) {
      return lead.manufacturers;
    }

    return lead?.manufacturer ? [lead.manufacturer] : [];
  };

  const buildExcelRows = (data) =>
    data.map((lead) => {
      const contact = Array.isArray(lead?.contacts) ? lead.contacts[0] : null;

      return {
        Lead_ID: lead.lead_id || "",
        Dealership: lead.company || "",

        "Contact First Name":
          contact?.first_name ||
          contact?.firstName ||
          lead.contact_first_name ||
          lead.contactFirstName ||
          "",

        "Contact Last Name":
          contact?.last_name ||
          contact?.lastName ||
          lead.contact_last_name ||
          lead.contactLastName ||
          "",

        "Contact Email":
          contact?.email || lead.contact_email || lead.contactEmail || "",

        "Contact Job Title": contact?.title || lead.contact_title || "",

        "Contact Home Phone":
          contact?.home_phone ||
          contact?.homePhone ||
          lead.contact_home_phone ||
          "",

        "Contact Work Phone":
          contact?.work_phone ||
          contact?.workPhone ||
          lead.contact_work_phone ||
          "",

        "Contact Mobile Phone":
          contact?.mobile_phone ||
          contact?.mobilePhone ||
          lead.contact_mobile_phone ||
          "",

        "Dealership Email": lead.email || "",
        "Sales Representative": lead.partner_name || lead.sales_rep_name || "",
        "Sales Representative Email": lead.partner_email || lead.sales_rep_email || "",

        "Lead Source": lead.lead_source || lead.leadSource || "",
        "Lead Type": lead.lead_type || lead.leadType || "",
        "Service Dashboard":
          lead.service_dashboard || lead.serviceDashboard || "",

        "Contact Date": formatDateOnly(lead.contact_date || lead.contactDate),

        Activity: lead.activity || "",
        "Activity Date": formatDateOnly(
          lead.activity_date || lead.last_contact,
        ),
        "Activity Notes": lead.activity_notes || "",

        "Next Activity": lead.active_next_activity?.activity_type || "",
        "Next Activity Date": formatDateOnly(
          lead.active_next_activity?.planned_date || lead.next_contact || "",
        ),
        "Next Activity Notes": lead.active_next_activity?.notes || "",

        "Opportunity Y/N": lead.opportunity_yn || lead.opportunityYn || "",
        Status: lead.status || "",

        "DMS Providers": getDmsProviders(lead).join(", "),
        "Other DMS Provider":
          lead.other_dms_provider || lead.otherDmsProvider || "",

        Products: getProducts(lead).join(", "),
        "Dealer Group": lead.dealer_group || lead.dealerGroup || "",
        Manufacturers: getManufacturers(lead).join(", "),
        "# of Rooftops": lead.rooftops ?? "",

        "Dealership Website": lead.website || "",
        "Dealership Main Line": lead.main_line || lead.mainLine || "",
        "Dealership Street Address":
          lead.street_address || lead.streetAddress || "",
        "Dealership City": lead.city || "",
        "Dealership State": lead.state || "",
        "Dealership ZIP": lead.zip_code || lead.zipCode || "",
        "Dealership Country": lead.country || "",

        "Demo Status": lead.demo_status || lead.demoStatus || "Not Booked",
        "Demo Scheduled Date": formatDateOnly(lead.demo_date || lead.demoDate),
        "Demo Scheduled Time": lead.demo_time || lead.demoTime || "",
        "Demo Timezone": lead.demo_timezone || "",
        "Created Date": formatTimestampUtc(lead.created_at),
        "Updated Date": formatTimestampUtc(lead.updated_at),
        "Next Steps": lead.next_steps || lead.nextSteps || "",
        Notes: lead.notes || "",
      };
    });

  const exportToExcel = async () => {
    setOpen(false);

    const data = await loadData();

    if (!data) {
      return;
    }

    try {
      setLoading(true);

      const rows = buildExcelRows(data);

      const worksheet = XLSX.utils.json_to_sheet(rows);

      worksheet["!cols"] = [
        { wch: 12 }, // Lead_ID
        { wch: 28 }, // Dealership
        { wch: 20 }, // Contact First Name
        { wch: 20 }, // Contact Last Name
        { wch: 28 }, // Contact Email
        { wch: 22 }, // Contact Job Title
        { wch: 22 }, // Contact Home Phone
        { wch: 22 }, // Contact Work Phone
        { wch: 22 }, // Contact Mobile Phone
        { wch: 28 }, // Dealership Email
        { wch: 24 }, // Sales Representative
        { wch: 32 }, // Sales Representative Email
        { wch: 18 }, // Lead Source
        { wch: 16 }, // Lead Type
        { wch: 22 }, // Service Dashboard
        { wch: 16 }, // Contact Date
        { wch: 24 }, // Activity
        { wch: 16 }, // Activity Date
        { wch: 35 }, // Activity Notes
        { wch: 24 }, // Next Activity
        { wch: 20 }, // Next Activity Date
        { wch: 35 }, // Next Activity Notes
        { wch: 16 }, // Opportunity Y/N
        { wch: 18 }, // Status
        { wch: 28 }, // DMS Providers
        { wch: 24 }, // Other DMS Provider
        { wch: 30 }, // Products
        { wch: 22 }, // Dealer Group
        { wch: 28 }, // Manufacturers
        { wch: 14 }, // # of Rooftops
        { wch: 26 }, // Dealership Website
        { wch: 24 }, // Dealership Main Line
        { wch: 30 }, // Dealership Street Address
        { wch: 18 }, // Dealership City
        { wch: 20 }, // Dealership State
        { wch: 12 }, // Dealership ZIP
        { wch: 16 }, // Dealership Country
        { wch: 18 }, // Demo Status
        { wch: 22 }, // Demo Scheduled Date
        { wch: 22 }, // Demo Scheduled Time
        { wch: 18 }, // Demo Timezone
        { wch: 18 }, // Created Date
        { wch: 18 }, // Updated Date
        { wch: 35 }, // Next Steps
        { wch: 35 }, // Notes
      ];

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "Leads");

      XLSX.writeFile(
        workbook,
        `leads_export_${new Date().toISOString().slice(0, 10)}.xlsx`,
      );
    } catch (err) {
      console.error("Excel export failed:", err);

      alert("Failed to export Excel file");
    } finally {
      setLoading(false);
    }
  };

  const exportToPDF = async () => {
    setOpen(false);

    const data = await loadData();

    if (!data) {
      return;
    }

    try {
      setLoading(true);

      const doc = new jsPDF("landscape", "mm", "a4");

      doc.setFontSize(16);
      doc.text("Leads Export", 14, 20);

      doc.setFontSize(10);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

      doc.text(`Total Leads: ${data.length}`, 14, 33);

      const tableData = data.map((lead) => [
        lead.company || "",
        getContactName(lead),
        lead.status || "",
        getDmsProviders(lead).join(", "),
        lead.demo_status || lead.demoStatus || "Not Booked",
      ]);

      autoTable(doc, {
        head: [
          ["Company", "Contact", "Status", "DMS Providers", "Demo Status"],
        ],
        body: tableData,
        startY: 38,
        styles: {
          fontSize: 8,
        },
        margin: {
          left: 14,
          right: 14,
        },
      });

      doc.save(`leads_export_${new Date().toISOString().slice(0, 10)}.pdf`);
    } catch (err) {
      console.error("PDF export failed:", err);

      alert("Failed to export PDF");
    } finally {
      setLoading(false);
    }
  };

  // In ExportButton.js - Update the render section

  return (
    <div
      ref={containerRef}
      className={`export-wrapper ${dropUp ? "export-wrapper-dropup" : ""}`}
    >
      <button
        type="button"
        className="btn btn-secondary btn-sm"
        disabled={loading || disabled}
        onClick={() => setOpen((prev) => !prev)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <i
          className={loading ? "fas fa-spinner fa-spin" : "fas fa-download"}
        ></i>
        {loading ? "Exporting..." : label}
        <i
          className={`fas fa-chevron-${open ? "up" : "down"}`}
          style={{ marginLeft: "4px", fontSize: "0.6rem" }}
        ></i>
      </button>

      {open && !loading && (
        <div className="export-dropdown-menu" role="menu">
          <button
            type="button"
            onClick={exportToExcel}
            className="export-menu-item"
            role="menuitem"
          >
            <i className="fas fa-file-excel"></i>
            <span>Excel (.xlsx)</span>
          </button>
          <button
            type="button"
            onClick={exportToPDF}
            className="export-menu-item"
            role="menuitem"
          >
            <i className="fas fa-file-pdf"></i>
            <span>PDF</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ExportButton;
